<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Progress from '@/components/forms/Progress.vue'
import { useProvidersStore } from '@/store/providers'
import { normalizeApiError } from '@/utils/apiError'
import {
  PROVIDER_IMPORT_FIELD_LIST,
  guessProviderImportMapping,
  isProviderImportConfirmAllowed,
  isProviderImportMappingComplete,
  readImportHeadersFromFile,
  validateImportFile,
} from '@/utils/providerImport.mjs'

const route = useRoute()
const router = useRouter()
const store = useProvidersStore()

const acceptedFileTypes = '.csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const step = ref('file')
const importFile = ref(null)
const fileInput = ref(null)
const headers = ref([])
const mapping = ref({})
const preview = ref(null)
const confirmResult = ref(null)
const refreshWarning = ref('')
const type = ref('')
const errorMessage = ref('')
const isReadingFile = ref(false)
const isPreviewLoading = ref(false)
const isConfirmLoading = ref(false)
const mappingSubmitted = ref(false)

const isBusy = computed(() => isReadingFile.value || isPreviewLoading.value || isConfirmLoading.value)
const mappingComplete = computed(() => isProviderImportMappingComplete(mapping.value))
const selectedMapping = computed(() => {
  return PROVIDER_IMPORT_FIELD_LIST.reduce((result, field) => {
    const header = String(mapping.value[field.key] || '').trim()
    if (header) {
      result[field.key] = header
    }
    return result
  }, {})
})
const canRequestPreview = computed(() => Boolean(importFile.value && headers.value.length > 0 && !isBusy.value))
const canConfirm = computed(() => isProviderImportConfirmAllowed(preview.value, isConfirmLoading.value))
const summary = computed(() => preview.value?.summary || {})
const previewItems = computed(() => Array.isArray(preview.value?.items) ? preview.value.items : [])
const skippedItems = computed(() => Array.isArray(preview.value?.skipped) ? preview.value.skipped : [])
const importErrors = computed(() => Array.isArray(preview.value?.errors) ? preview.value.errors : [])
const mappingErrors = computed(() => importErrors.value.filter((error) => error.scope === 'mapping'))
const generalErrors = computed(() => importErrors.value.filter((error) => !['mapping', 'row'].includes(error.scope)))
const rowErrorGroups = computed(() => {
  const groups = importErrors.value
    .filter((error) => error.scope === 'row')
    .reduce((result, error) => {
      const key = error.rowNumber || 'row'
      if (!result[key]) {
        result[key] = []
      }
      result[key].push(error)
      return result
    }, {})

  return Object.entries(groups).map(([rowNumber, errors]) => ({ rowNumber, errors }))
})
const previewStatusClass = computed(() => preview.value?.status === 'ready' ? 'text-success' : 'text-danger')
const confirmDisabledReason = computed(() => {
  if (!preview.value?.sessionId) return 'Session ID відсутній.'
  if (preview.value.status !== 'ready') return 'Preview має бути у статусі ready.'
  if (Number(summary.value.errorCount || 0) > 0) return 'Виправте помилки перед підтвердженням.'
  if (isConfirmLoading.value) return 'Підтвердження виконується.'
  return ''
})

function clearFeedback() {
  type.value = ''
  errorMessage.value = ''
}

function resetPreviewState() {
  preview.value = null
  confirmResult.value = null
  refreshWarning.value = ''
}

function resetImport() {
  clearFeedback()
  step.value = 'file'
  importFile.value = null
  headers.value = []
  mapping.value = {}
  mappingSubmitted.value = false
  resetPreviewState()

  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

function hasRequiredFieldError(field) {
  return mappingSubmitted.value && field.required && !mapping.value[field.key]
}

function applyError(error, fallbackMessage) {
  const apiError = normalizeApiError(error, { fallbackMessage })
  errorMessage.value = apiError.message
  type.value = 'error'
}

async function onFileChange(event) {
  clearFeedback()
  resetPreviewState()
  mappingSubmitted.value = false

  const [file] = event.target.files || []
  importFile.value = file || null
  headers.value = []
  mapping.value = {}

  const validation = validateImportFile(importFile.value)
  if (!validation.valid) {
    errorMessage.value = validation.message
    type.value = 'error'
    return
  }

  isReadingFile.value = true
  type.value = 'loading'

  try {
    const parsedHeaders = await readImportHeadersFromFile(importFile.value)

    if (!parsedHeaders.length) {
      throw new Error('Не вдалося знайти заголовки у першому непорожньому рядку файлу.')
    }

    headers.value = parsedHeaders
    mapping.value = guessProviderImportMapping(parsedHeaders)
    step.value = 'mapping'
    type.value = ''
  } catch (error) {
    errorMessage.value = error?.message || 'Не вдалося прочитати заголовки файлу.'
    type.value = 'error'
  } finally {
    isReadingFile.value = false
  }
}

async function requestPreview() {
  mappingSubmitted.value = true
  clearFeedback()
  resetPreviewState()

  if (!mappingComplete.value) {
    errorMessage.value = 'Оберіть колонки для обовʼязкових полів.'
    type.value = 'error'
    return
  }

  isPreviewLoading.value = true
  type.value = 'loading'

  try {
    preview.value = await store.previewProvidersImport(importFile.value, selectedMapping.value)
    step.value = 'preview'
    type.value = ''
  } catch (error) {
    applyError(error, 'Не вдалося підготувати preview імпорту.')
  } finally {
    isPreviewLoading.value = false
  }
}

async function confirmImport() {
  if (!canConfirm.value) {
    return
  }

  clearFeedback()
  isConfirmLoading.value = true
  type.value = 'loading'

  try {
    const response = await store.confirmProvidersImport(preview.value.sessionId)
    confirmResult.value = response.result || {}
    step.value = 'success'
    type.value = 'success'

    try {
      await store.getProviders(1)
    } catch (error) {
      refreshWarning.value = normalizeApiError(error, {
        fallbackMessage: 'Імпорт виконано, але список постачальників не вдалося оновити автоматично.',
      }).message
    }
  } catch (error) {
    applyError(error, 'Не вдалося підтвердити імпорт постачальників.')
  } finally {
    isConfirmLoading.value = false
  }
}

function goToProvidersList() {
  router.push({ name: 'providers' })
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') {
    return '-'
  }
  return String(value)
}

function formatAction(action) {
  const labels = {
    create: 'Створення',
    update: 'Оновлення',
  }
  return labels[action] || action || '-'
}

function formatError(error) {
  const field = error.field ? `${error.field}: ` : ''
  return `${field}${error.message || 'Помилка'}`
}
</script>

<template>
  <div class="container mt-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2>{{ route.meta.title }}</h2>
      <div class="d-flex align-items-center gap-2">
        <Progress
          :type="type"
          :error-message="errorMessage"
          loading-message="Обробка імпорту..."
          success-message="Імпорт виконано!"
        />
        <button class="btn btn-outline-secondary" type="button" @click="goToProvidersList" :disabled="isBusy">
          <i class="bi bi-arrow-left"></i>
        </button>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-body">
        <ul class="nav nav-pills import-steps mb-4">
          <li class="nav-item">
            <span class="nav-link" :class="{ active: step === 'file' }">1. Файл</span>
          </li>
          <li class="nav-item">
            <span class="nav-link" :class="{ active: step === 'mapping', disabled: headers.length === 0 }">2. Mapping</span>
          </li>
          <li class="nav-item">
            <span class="nav-link" :class="{ active: step === 'preview', disabled: !preview }">3. Preview</span>
          </li>
          <li class="nav-item">
            <span class="nav-link" :class="{ active: step === 'success', disabled: !confirmResult }">4. Result</span>
          </li>
        </ul>

        <template v-if="step === 'file'">
          <div class="row mb-3">
            <div class="col-md-4 d-flex justify-content-end align-items-center">
              Файл CSV/XLSX
            </div>
            <div class="col-md-8">
              <input
                ref="fileInput"
                class="form-control"
                type="file"
                :accept="acceptedFileTypes"
                :disabled="isBusy"
                @change="onFileChange"
              />
              <div class="form-text">Максимальний розмір: 10 MB.</div>
            </div>
          </div>
        </template>

        <template v-if="step === 'mapping'">
          <div class="row mb-3">
            <div class="col-md-4 d-flex justify-content-end align-items-start pt-2">
              Колонки файлу
            </div>
            <div class="col-md-8">
              <div class="headers-list">
                <span v-for="header in headers" :key="header" class="badge text-bg-light border">
                  {{ header }}
                </span>
              </div>
            </div>
          </div>

          <div v-for="field in PROVIDER_IMPORT_FIELD_LIST" :key="field.key" class="row mb-3">
            <div class="col-md-4 d-flex justify-content-end align-items-center">
              {{ field.label }}
              <span v-if="field.required" class="text-danger ms-1">*</span>
            </div>
            <div class="col-md-8">
              <select
                v-model="mapping[field.key]"
                class="form-select"
                :class="{ 'is-invalid': hasRequiredFieldError(field) }"
                :disabled="isBusy"
              >
                <option value="">-- Не імпортувати --</option>
                <option v-for="header in headers" :key="`${field.key}-${header}`" :value="header">
                  {{ header }}
                </option>
              </select>
              <div v-if="hasRequiredFieldError(field)" class="invalid-feedback d-block">
                Обовʼязкове поле.
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2">
            <button class="btn btn-outline-secondary" type="button" @click="resetImport" :disabled="isBusy">
              <i class="bi bi-arrow-left"></i>
            </button>
            <button class="btn btn-primary" type="button" @click="requestPreview" :disabled="!canRequestPreview">
              <i class="bi bi-eye"></i>
              <span class="ms-2">Preview</span>
            </button>
          </div>
        </template>

        <template v-if="step === 'preview' && preview">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <span class="fw-semibold">Status:</span>
              <span :class="previewStatusClass">{{ preview.status }}</span>
            </div>
            <div class="text-muted small">Session: {{ preview.sessionId || '-' }}</div>
          </div>

          <div class="summary-grid mb-4">
            <div class="summary-item">
              <div class="summary-label">Rows</div>
              <div class="summary-value">{{ summary.totalRows || 0 }}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Create</div>
              <div class="summary-value">{{ summary.createCount || 0 }}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Update</div>
              <div class="summary-value">{{ summary.updateCount || 0 }}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Skipped</div>
              <div class="summary-value">{{ summary.skippedCount || 0 }}</div>
            </div>
            <div class="summary-item" :class="{ 'summary-error': Number(summary.errorCount || 0) > 0 }">
              <div class="summary-label">Errors</div>
              <div class="summary-value">{{ summary.errorCount || 0 }}</div>
            </div>
          </div>

          <div class="table-responsive mb-4">
            <table class="table table-hover align-middle">
              <thead class="table-light">
              <tr>
                <th>Row</th>
                <th>Action</th>
                <th>External code</th>
                <th>Name</th>
                <th>Markup</th>
                <th>Exchange rate</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="item in previewItems" :key="`${item.rowNumber}-${item.externalCode}`">
                <td>{{ item.rowNumber }}</td>
                <td>{{ formatAction(item.action) }}</td>
                <td>{{ formatValue(item.externalCode) }}</td>
                <td>{{ formatValue(item.name) }}</td>
                <td>{{ formatValue(item.markup) }}</td>
                <td>{{ formatValue(item.exchangeRate) }}</td>
              </tr>
              <tr v-if="previewItems.length === 0">
                <td colspan="6" class="text-center text-muted">Немає рядків для імпорту.</td>
              </tr>
              </tbody>
            </table>
          </div>

          <div v-if="skippedItems.length > 0" class="mb-4">
            <h5>Skipped duplicates</h5>
            <div class="table-responsive">
              <table class="table table-sm align-middle">
                <thead class="table-light">
                <tr>
                  <th>Row</th>
                  <th>External code</th>
                  <th>Overridden by row</th>
                  <th>Reason</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="item in skippedItems" :key="`${item.rowNumber}-${item.overriddenByRowNumber}`">
                  <td>{{ item.rowNumber }}</td>
                  <td>{{ formatValue(item.externalCode) }}</td>
                  <td>{{ item.overriddenByRowNumber || '-' }}</td>
                  <td>{{ item.reason || '-' }}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="importErrors.length > 0" class="mb-4">
            <h5>Errors</h5>
            <div v-if="mappingErrors.length > 0" class="alert alert-danger">
              <div class="fw-semibold mb-2">Mapping</div>
              <ul class="mb-0">
                <li v-for="(error, index) in mappingErrors" :key="`mapping-${index}`">
                  {{ formatError(error) }}
                </li>
              </ul>
            </div>
            <div v-for="group in rowErrorGroups" :key="group.rowNumber" class="alert alert-danger">
              <div class="fw-semibold mb-2">Row {{ group.rowNumber }}</div>
              <ul class="mb-0">
                <li v-for="(error, index) in group.errors" :key="`row-${group.rowNumber}-${index}`">
                  {{ formatError(error) }}
                </li>
              </ul>
            </div>
            <div v-if="generalErrors.length > 0" class="alert alert-danger">
              <ul class="mb-0">
                <li v-for="(error, index) in generalErrors" :key="`general-${index}`">
                  {{ formatError(error) }}
                </li>
              </ul>
            </div>
          </div>

          <div v-if="confirmDisabledReason" class="alert alert-warning">
            {{ confirmDisabledReason }}
          </div>

          <div class="d-flex justify-content-end gap-2">
            <button class="btn btn-outline-secondary" type="button" @click="step = 'mapping'" :disabled="isBusy">
              <i class="bi bi-arrow-left"></i>
            </button>
            <button class="btn btn-primary" type="button" @click="confirmImport" :disabled="!canConfirm">
              <i class="bi bi-check2-circle"></i>
              <span class="ms-2">Confirm</span>
            </button>
          </div>
        </template>

        <template v-if="step === 'success'">
          <div class="alert alert-success">
            Імпорт постачальників підтверджено.
          </div>

          <div class="summary-grid mb-4">
            <div class="summary-item">
              <div class="summary-label">Created</div>
              <div class="summary-value">{{ confirmResult?.created || 0 }}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Updated</div>
              <div class="summary-value">{{ confirmResult?.updated || 0 }}</div>
            </div>
            <div class="summary-item">
              <div class="summary-label">Total</div>
              <div class="summary-value">{{ confirmResult?.total || 0 }}</div>
            </div>
          </div>

          <div v-if="refreshWarning" class="alert alert-warning">
            {{ refreshWarning }}
          </div>

          <div class="d-flex justify-content-end gap-2">
            <button class="btn btn-outline-secondary" type="button" @click="resetImport">
              <i class="bi bi-arrow-repeat"></i>
              <span class="ms-2">Новий імпорт</span>
            </button>
            <button class="btn btn-primary" type="button" @click="goToProvidersList">
              <i class="bi bi-list-ul"></i>
              <span class="ms-2">До списку</span>
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.import-steps .nav-link {
  color: #034c48;
  cursor: default;
}

.import-steps .nav-link.active {
  background-color: #034c48;
  color: #fff;
}

.headers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.75rem;
}

.summary-item {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0.75rem;
  background: #f8f9fa;
}

.summary-error {
  border-color: #dc3545;
  background: #fff5f5;
}

.summary-label {
  color: #6c757d;
  font-size: 0.875rem;
}

.summary-value {
  font-size: 1.35rem;
  font-weight: 600;
}
</style>
